/**
 * @praesto/core/internal/firestore
 *
 * Firebase SDK substrate: one app + Auth/Firestore/Storage handles,
 * emulator-aware. Pure SDK/config glue — its real behaviour is exercised by
 * the Phase 2 subsystems against the Firebase Emulator Suite, not unit-tested
 * here (no value in asserting the SDK initialises itself).
 */
import { type FirebaseApp, getApps, initializeApp } from "firebase/app";
import { type Auth, connectAuthEmulator, getAuth } from "firebase/auth";
import { type Firestore, connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { type FirebaseStorage, connectStorageEmulator, getStorage } from "firebase/storage";

export interface FirebaseConfig {
  readonly apiKey: string;
  readonly authDomain: string;
  readonly projectId: string;
  readonly appId: string;
  readonly storageBucket: string;
  // Required by FCM (getMessaging/getToken). Auth/Firestore/Storage don't need
  // it, so its absence stays invisible until web push is enabled.
  readonly messagingSenderId: string;
}

export interface EmulatorConfig {
  readonly auth?: string; // e.g. 'http://127.0.0.1:9099'
  readonly firestore?: string; // e.g. '127.0.0.1:8080'
  readonly storage?: string; // e.g. '127.0.0.1:9199'
}

export interface FirebaseHandles {
  readonly app: FirebaseApp;
  readonly auth: Auth;
  readonly db: Firestore;
  readonly storage: FirebaseStorage;
}

const APP_NAME = "praesto";
const emulatorsConnected = new WeakSet<FirebaseApp>();

function splitHostPort(value: string): { host: string; port: number } {
  const cleaned = value.replace(/^https?:\/\//, "");
  const [host, port] = cleaned.split(":");
  return { host: host || "127.0.0.1", port: Number(port) };
}

export function initFirebase(config: FirebaseConfig, emulators?: EmulatorConfig): FirebaseHandles {
  const existing = getApps().find((a) => a.name === APP_NAME);
  const app = existing ?? initializeApp(config, APP_NAME);

  const auth = getAuth(app);
  const db = getFirestore(app);
  const storage = getStorage(app);

  if (emulators && !emulatorsConnected.has(app)) {
    if (emulators.auth) {
      connectAuthEmulator(auth, emulators.auth, { disableWarnings: true });
    }
    if (emulators.firestore) {
      const { host, port } = splitHostPort(emulators.firestore);
      connectFirestoreEmulator(db, host, port);
    }
    if (emulators.storage) {
      const { host, port } = splitHostPort(emulators.storage);
      connectStorageEmulator(storage, host, port);
    }
    emulatorsConnected.add(app);
  }

  return { app, auth, db, storage };
}
